import { Box, Center, Checkbox, HStack, Input, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useInViewport } from "react-in-viewport"
import { useZoneGroupsByKeywordQuery, ZoneGroupsByKeywordQueryVariables } from "../../graphql"

export type ZoneGroupSelectorProps = {
	onUpdate: (zoneGroupIds: string[]) => void
	value: string[]
}

export const ZoneGroupSelector: React.FC<ZoneGroupSelectorProps> = ({ value, onUpdate }) => {
	const [variables, setVariables] = useState<ZoneGroupsByKeywordQueryVariables>({ keyword: "", pagination: { limit: 20, page: 1 } })

	const [{ data, fetching, error }] = useZoneGroupsByKeywordQuery({ variables })

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.zoneGroupsByKeyword.hasNextPage) {
			setVariables((prev) => ({
				...prev,
				pagination: {
					...prev.pagination,
					page: (prev.pagination.page || 0) + 1,
				},
			}))
		}
	}, [data, fetching])

	const ref = useRef()

	const { inViewport } = useInViewport(ref as any, { threshold: 0.25 })

	useEffect(() => {
		if (inViewport) {
			nextPage()
		}
	}, [inViewport])

	const [keyword, setKeyword] = useState("")

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setVariables((prev) => ({
				...prev,
				keyword,
				pagination: { ...prev.pagination, page: 1 },
			}))
		}, 400)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword])

	const handleChange = (ZoneId: string, isChecked: boolean) => {
		if (isChecked) {
			onUpdate([...value, ZoneId])
		} else {
			const _value = [...value]
			_value.splice(_value.indexOf(ZoneId), 1)
			onUpdate(_value)
		}
	}

	const isZoneGroupselected = useCallback((ZoneId: string) => value.includes(ZoneId), [value])

	const isAllSelected = useMemo(() => data?.zoneGroupsByKeyword.zoneGroups.map((u) => u._id).every((uid) => value.includes(uid)), [data, value])
	const isNoneSelected = useMemo(() => !value.length, [data, value])

	return (
		<VStack w="full" align="stretch">
			<HStack w="full" flexWrap="wrap" justify="space-between">
				<Input flex="1" variant="filled" bgColor="grayscale.input-background" placeholder="Search" _placeholder={{ color: "grayscale.placeholer" }} value={keyword} onChange={(e) => setKeyword(e.target.value)} />
			</HStack>
			{fetching && !data?.zoneGroupsByKeyword.zoneGroups.length ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading zone groups</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : !data?.zoneGroupsByKeyword.zoneGroups.length ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any zone groups.
					</Text>
				</Center>
			) : (
				<VStack w="full" align="stretch" h="full" maxH={{ base: "sm", xl: "lg" }} overflowY="auto" spacing={0}>
					<Table>
						<Thead pos="sticky" top="0" w="full" zIndex={10} bgColor="white">
							<Tr>
								<Th>
									<Checkbox
										isDisabled={data.zoneGroupsByKeyword.hasNextPage}
										isChecked={isAllSelected}
										isIndeterminate={!isAllSelected && !isNoneSelected}
										onChange={(e) => {
											e.target.checked ? onUpdate(data.zoneGroupsByKeyword.zoneGroups.map((r) => r._id)) : onUpdate([])
										}}
									>
										<Text fontSize="xs" textTransform="lowercase">
											{value.length} selected
										</Text>
									</Checkbox>
								</Th>
								<Th>Zone</Th>
							</Tr>
						</Thead>
						<Tbody>
							{data?.zoneGroupsByKeyword.zoneGroups.map((zoneGroup) => (
								<Tr key={zoneGroup._id}>
									<Td>
										<Checkbox isChecked={isZoneGroupselected(zoneGroup._id)} onChange={(e) => handleChange(zoneGroup._id, e.target.checked)} />
									</Td>
									<Td>{zoneGroup.label.name}</Td>
								</Tr>
							))}
							<Box w="full" h="2" ref={ref as any} />
						</Tbody>
					</Table>
					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more zone groups</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			)}
		</VStack>
	)
}
